import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  IconButton,
  Typography,
  Stack,
  Chip,
  Card,
  Badge,
  Tooltip,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  OpenInNew as OpenInNewIcon,
  SmsOutlined as SmsOutlinedIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import NiceModal from '@ebay/nice-modal-react';
import { matchPath, useLocation } from 'react-router-dom';
import chatbotService, {
  RevaAction,
  RevaActionObject,
  RevaHandoff,
} from '../../api/services/chatbot';
import { smsHref } from '../../utils/contactHomepoint';
import RevaActionCard, {
  RevaActionState,
  RevaObjectCard,
} from './RevaActionCard';
import { sanitizePropertyData } from '../../utils/sanitize';
import { useAuth } from '../../contexts/AuthContext';
import { useReva } from '../../contexts/RevaContext';
import PropertyInfoModal from '../PropertyMap/PropertyInfoModal';
import { routes } from '../../routes/app';
import {
  REVA_RAIL_WIDTH,
  RevaPlacement,
  useRevaDockedWidth,
  useRevaPlacement,
  useRevaShellDocked,
} from '../../hooks/useRevaPlacement';

/** Side-aware prompt: a seller is asked about their sale, a buyer about their search. */
export const askPlaceholder = (
  side?: 'buying' | 'selling' | 'both' | 'none'
): string => {
  if (side === 'selling') return 'Ask Reva anything about your sale…';
  if (side === 'buying') return 'Ask Reva anything about your search…';
  return 'Ask Reva anything about buying or selling…';
};

/** Human label for the page the question is being asked from. */
const routeLabel = (pathname: string): string | undefined => {
  const match = routes.find(
    (route) => route.path && matchPath(route.path, pathname)
  );
  return match?.title || undefined;
};

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  // The dock is intentionally narrow. Keep the thread dense enough that a
  // financial summary can be understood without every line becoming a scroll.
  padding: theme.spacing(1.25),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  maxWidth: isUser ? '85%' : '100%',
  width: isUser ? 'auto' : '100%',
  padding: theme.spacing(1.25),
  borderRadius: theme.spacing(2),
  backgroundColor: isUser
    ? theme.palette.primary.main
    : theme.palette.grey[100],
  color: isUser ? 'white' : theme.palette.text.primary,
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  wordBreak: 'break-word',
}));

interface PropertyResult {
  id: string;
  mlsNumber: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  lat?: number;
  lng?: number;
  price: number;
  beds: number;
  baths: number;
  sqft: number;
  propertyType: string;
  photos: string[];
  description?: string;
  daysOnMarket?: number;
  listingAgent?: string;
  subdivision?: string;
  source?: string;
  listingOfficeName?: string;
  listingAgentPhone?: string;
  listingAgentOfficePhone?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  properties?: PropertyResult[];
  /** A side effect Reva proposed on this turn. Nothing runs until the buyer confirms it. */
  action?: RevaAction;
  /** Objects Reva cited on this turn, shown as read-only cards. */
  objects?: RevaActionObject[];
  /** Reva declined to answer and pointed the buyer to a person. */
  handoff?: RevaHandoff;
}

interface ActionCardStatus {
  state: RevaActionState;
  /** What the tool reported once it ran, in its own words. */
  resultMessage: string | null;
}

interface Suggestion {
  label: string;
  question: string;
}

interface RevaPanelProps {
  open: boolean;
  onClose: () => void;
  context?: string;
  /** Question to send automatically once the conversation opens (from a chip). */
  initialQuestion?: string | null;
  onInitialQuestionConsumed?: () => void;
}

const DEFAULT_SUGGESTIONS: Suggestion[] = [
  'Show me homes in Hoover AL with 3 beds, pool, and basement',
  'How much can I save with Homepoint?',
  'How much mortgage can I afford?',
  'Best neighborhoods with top schools',
].map((question) => ({ label: question, question }));

const GENERIC_WELCOME = `### Hi! I'm Reva, your AI real estate assistant

I can help you with:

**Search for properties** in Alabama and Tennessee
**Learn about neighborhoods, schools, and local amenities**
**Understand** the home buying and selling process
**Explore your options** including our $5,000 flat fee service
**Answer your real estate questions** anytime

**What can I help you with today?**`;

/** Placement-specific geometry for the panel surface. Flat in both modes: nothing floats. */
const surfaceSx = (placement: RevaPlacement, dockedWidth: number) => {
  if (placement === 'sheet') {
    return {
      position: 'fixed' as const,
      inset: 0,
      width: '100vw',
      zIndex: 1300,
      borderRadius: 0,
      boxShadow: 'none',
    };
  }
  // Full height, like the nav rail on the left: the page header only spans the
  // column between them, so anything below it would leave a gap up top.
  return {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    width: dockedWidth,
    zIndex: 1200,
    borderRadius: 0,
    // The docked column is part of the page, not a window floating over it.
    boxShadow: 'none',
  };
};

/**
 * Reva's one conversation surface. The same anatomy — header, grounding line,
 * "Viewing" context chip, thread, composer — in both placements; only the
 * geometry changes with the viewport.
 *
 * The panel stays mounted while it is closed or collapsed, so the thread, the
 * conversation id and any pending action card survive dismissal, navigation and
 * a resize across breakpoints. Only a change of signed-in user resets it, via
 * the `key` the provider supplies.
 */
const RevaPanel: React.FC<RevaPanelProps> = ({
  open,
  onClose,
  context = 'general',
  initialQuestion = null,
  onInitialQuestionConsumed,
}) => {
  const theme = useTheme();
  const placement = useRevaPlacement();
  const dockedWidth = useRevaDockedWidth();
  const shellDocked = useRevaShellDocked();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [actionStatuses, setActionStatuses] = useState<
    Record<string, ActionCardStatus>
  >({});
  /** The buyer dismissed the "Viewing" chip: stop grounding messages in this page. */
  const [contextCleared, setContextCleared] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const propertyListRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const assistantMessageRefs = useRef<Record<string, HTMLElement | null>>({});

  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const { briefing, briefingLoading, openReva, unseenCount, notifyReply } =
    useReva();
  const location = useLocation();
  const initialQuestionSent = useRef<string | null>(null);

  const pageLabel = routeLabel(location.pathname);
  const pageListingId =
    matchPath('/buyers/:id/listing/:listingId', location.pathname)?.params
      .listingId ??
    matchPath('/admin/buyers/:id/listing/:listingId', location.pathname)?.params
      .listingId;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // A new page re-grounds the conversation: the chip comes back for the fresh route.
  useEffect(() => {
    setContextCleared(false);
  }, [location.pathname]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      setTimeout(() => {
        const assistantElement = assistantMessageRefs.current[lastMessage.id];
        if (assistantElement) {
          assistantElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    } else if (lastMessage && lastMessage.role === 'user') {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, 100);
  }, [open]);

  useEffect(() => {
    if (!open || messages.length !== 0) return;
    // For signed-in customers, wait for the briefing so the opener reflects their live state.
    if (userId && briefingLoading && !briefing) return;

    const content = briefing
      ? `**${briefing.greeting}.** ${briefing.summary}`
      : GENERIC_WELCOME;

    setMessages([
      { id: 'welcome', role: 'assistant', content, timestamp: new Date() },
    ]);
  }, [open, messages.length, userId, briefing, briefingLoading]);

  const setActionStatus = useCallback(
    (pendingActionId: string, status: ActionCardStatus) => {
      setActionStatuses((prev) => ({ ...prev, [pendingActionId]: status }));
    },
    []
  );

  const submitQuestion = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || loading) return;

      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: question,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setLoading(true);
      // The server clears the parked action on every turn, so any open card is settled by this
      // message — including a typed "yes". Reva's reply is what says which way it went. If the
      // request never reaches the server the action is still parked, so remember what was
      // closed here and give those cards their controls back on failure.
      const closedByThisTurn: string[] = [];
      setActionStatuses((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          if (next[id].state === 'pending') {
            closedByThisTurn.push(id);
            next[id] = { state: 'closed', resultMessage: null };
          }
        });
        return next;
      });

      try {
        const response = await chatbotService.sendMessage({
          message: question,
          conversationId: conversationId || undefined,
          context,
          userId,
          // Dismissing the "Viewing" chip takes the page out of the payload entirely.
          ...(contextCleared
            ? {}
            : {
                pageContext: {
                  route: location.pathname,
                  ...(pageLabel ? { label: pageLabel } : {}),
                  ...(pageListingId ? { listingId: pageListingId } : {}),
                },
              }),
        });

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.response,
          timestamp: new Date(),
          properties: response.properties?.map(sanitizePropertyData),
          action: response.action,
          objects: response.objects,
          handoff: response.handoff,
        };

        setMessages((prev) => [...prev, assistantMessage]);
        // Badges the toolbar button and the rail when the reply lands on a closed panel.
        notifyReply();

        if (response.action) {
          setActionStatus(response.action.pendingActionId, {
            state: 'pending',
            resultMessage: null,
          });
        }

        if (!conversationId && response.conversationId) {
          setConversationId(response.conversationId);
        }
      } catch (error) {
        console.error('Error sending message:', error);

        // The turn never landed, so the action may well still be parked. Give the card its
        // controls back rather than stranding the buyer with a proposal they cannot answer.
        if (closedByThisTurn.length > 0) {
          setActionStatuses((prev) => {
            const next = { ...prev };
            closedByThisTurn.forEach((id) => {
              if (next[id]?.state === 'closed') {
                next[id] = { state: 'pending', resultMessage: null };
              }
            });
            return next;
          });
        }

        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content:
            'I apologize, but I encountered an error. Please try again later.',
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setLoading(false);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    },
    [
      loading,
      conversationId,
      context,
      userId,
      location.pathname,
      pageLabel,
      pageListingId,
      contextCleared,
      setActionStatus,
      notifyReply,
    ]
  );

  const sendMessage = useCallback(
    () => submitQuestion(input),
    [submitQuestion, input]
  );

  const appendAssistantMessage = useCallback((content: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-action`,
        role: 'assistant',
        content,
        timestamp: new Date(),
      },
    ]);
  }, []);

  /**
   * The buyer's own tap is the only thing that runs a parked action. Signed-in buyers go through
   * the confirm rail, which claims the action once; anonymous visitors have no conversation the
   * server can attribute to them, so they fall back to the typed-yes path.
   */
  const handleConfirmAction = useCallback(
    async (action: RevaAction) => {
      if (!userId || !conversationId) {
        setActionStatus(action.pendingActionId, {
          state: 'closed',
          resultMessage: null,
        });
        submitQuestion('yes');
        return;
      }

      setActionStatus(action.pendingActionId, {
        state: 'working',
        resultMessage: null,
      });

      try {
        const result = await chatbotService.resolveAction(
          action.pendingActionId,
          { conversationId, decision: 'confirm' }
        );
        setActionStatus(action.pendingActionId, {
          state: result.status === 'confirmed' ? 'confirmed' : 'failed',
          resultMessage: null,
        });
        appendAssistantMessage(result.message);
      } catch (error) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
          "I couldn't complete that. Ask me again and I'll set it up.";
        setActionStatus(action.pendingActionId, {
          state: 'failed',
          resultMessage: message,
        });
      }
    },
    [
      userId,
      conversationId,
      setActionStatus,
      submitQuestion,
      appendAssistantMessage,
    ]
  );

  /** Best-effort release of the parked action; the next message would clear it anyway. */
  const releaseAction = useCallback(
    async (action: RevaAction) => {
      if (!userId || !conversationId) return;
      try {
        await chatbotService.resolveAction(action.pendingActionId, {
          conversationId,
          decision: 'cancel',
        });
      } catch (error) {
        console.error('Failed to release the pending Reva action:', error);
      }
    },
    [userId, conversationId]
  );

  const handleCancelAction = useCallback(
    (action: RevaAction) => {
      setActionStatus(action.pendingActionId, {
        state: 'cancelled',
        resultMessage: null,
      });
      releaseAction(action);
    },
    [setActionStatus, releaseAction]
  );

  const handleChangeAction = useCallback(
    (action: RevaAction) => {
      setActionStatus(action.pendingActionId, {
        state: 'changing',
        resultMessage: null,
      });
      releaseAction(action);
      setInput(action.editPrompt);
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [setActionStatus, releaseAction]
  );

  // Send a question handed in from a briefing or activity chip once the opener is in place.
  useEffect(() => {
    // The panel outlives any one hand-off now, so the guard has to be released
    // once the question has been consumed. Otherwise picking the same chip a
    // second time would match the stale ref and silently send nothing.
    if (!initialQuestion) {
      initialQuestionSent.current = null;
      return;
    }
    if (!open || loading) return;
    if (messages.length === 0) return;
    if (initialQuestionSent.current === initialQuestion) return;
    initialQuestionSent.current = initialQuestion;
    submitQuestion(initialQuestion);
    onInitialQuestionConsumed?.();
  }, [
    open,
    initialQuestion,
    loading,
    messages.length,
    submitQuestion,
    onInitialQuestionConsumed,
  ]);

  const renderActionCard = (action: RevaAction) => {
    const status = actionStatuses[action.pendingActionId];
    return (
      <Box sx={{ width: '100%', mt: 1.5 }}>
        <RevaActionCard
          action={action}
          state={status?.state ?? 'closed'}
          resultMessage={status?.resultMessage ?? null}
          onConfirm={() => handleConfirmAction(action)}
          onChange={() => handleChangeAction(action)}
          onCancel={() => handleCancelAction(action)}
        />
      </Box>
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions: Suggestion[] = briefing?.chips?.length
    ? briefing.chips
    : DEFAULT_SUGGESTIONS;

  const handlePropertyClick = (property: PropertyResult) => {
    try {
      NiceModal.show(PropertyInfoModal, { mlsListingId: property.id });
    } catch (error) {
      console.error('Error showing property modal:', error);
    }
  };

  const isDocked = placement === 'docked';
  // The rail only stands in for a column the shell actually reserves room for.
  const showRail = shellDocked && !open;

  return (
    <>
      {showRail && (
        <Box
          data-testid="reva-rail"
          role="button"
          tabIndex={0}
          aria-label="Open Reva"
          onClick={() => openReva()}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openReva();
            }
          }}
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: REVA_RAIL_WIDTH,
            zIndex: 1200,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            pt: 2,
            backgroundColor: theme.palette.background.paper,
            borderLeft: `1px solid ${theme.palette.divider}`,
            '&:hover': { backgroundColor: theme.palette.action.hover },
          }}
        >
          <Badge
            data-testid="reva-rail-badge"
            badgeContent={unseenCount}
            color="primary"
            overlap="circular"
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.main + '14',
              }}
            >
              <img
                src="/reva-icon-colored.png"
                alt=""
                style={{ width: 22, height: 22 }}
              />
            </Box>
          </Badge>
          <Typography
            variant="caption"
            sx={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              letterSpacing: 2,
              fontWeight: 700,
              color: 'text.secondary',
            }}
          >
            REVA
          </Typography>
        </Box>
      )}

      <Paper
        elevation={0}
        square
        data-testid="reva-chat"
        data-placement={placement}
        data-state={open ? 'open' : 'closed'}
        sx={{
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          borderLeft:
            placement === 'sheet'
              ? 'none'
              : `1px solid ${theme.palette.divider}`,
          ...surfaceSx(placement, dockedWidth),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.main + '14',
              }}
            >
              <img
                src="/reva-icon-colored.png"
                alt=""
                style={{ width: 22, height: 22 }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                Reva
              </Typography>
              {briefing && briefing.knows.length > 0 && (
                <Typography
                  data-testid="reva-knows-pill"
                  variant="caption"
                  noWrap
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  Knows {briefing.knows.join(' · ')}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            data-testid={isDocked ? 'reva-panel-collapse' : 'reva-panel-close'}
            aria-label={isDocked ? 'Collapse Reva' : 'Close Reva'}
          >
            {isDocked ? <ChevronRightIcon /> : <CloseIcon />}
          </IconButton>
        </Box>

        {!contextCleared && pageLabel && (
          <Box
            data-testid="reva-viewing-chip"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                flexShrink: 0,
              }}
            >
              Viewing
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: 'text.primary', fontWeight: 600, minWidth: 0 }}
            >
              {pageLabel}
            </Typography>
            <Tooltip title="Stop using this page as context">
              <IconButton
                size="small"
                data-testid="reva-viewing-clear"
                aria-label="Clear page context"
                onClick={() => setContextCleared(true)}
                sx={{ ml: 'auto', p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <MessagesContainer>
          {messages.map((message) => (
            <Box
              key={message.id}
              ref={(el: HTMLDivElement | null) => {
                if (message.role === 'assistant' && el) {
                  assistantMessageRefs.current[message.id] = el;
                }
              }}
              display="flex"
              flexDirection="column"
              alignItems={message.role === 'user' ? 'flex-end' : 'flex-start'}
              width="100%"
            >
              <MessageBubble isUser={message.role === 'user'}>
                {message.role === 'user' ? (
                  <Typography
                    variant="body2"
                    style={{ whiteSpace: 'pre-line', fontSize: '0.875rem' }}
                  >
                    {message.content}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      fontSize: '0.875rem',
                      '& h1': {
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        mb: 0.75,
                        mt: 0,
                      },
                      '& h2': {
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        mb: 0.75,
                        mt: 1.25,
                      },
                      '& h3': {
                        fontSize: '1rem',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        mb: 0.75,
                        mt: 1.25,
                      },
                      '& h2:first-child': { mt: 0 },
                      '& h3:first-child': { mt: 0 },
                      '& p': { mb: 0.75, mt: 0, lineHeight: 1.5 },
                      '& ul': { pl: 2.25, mb: 0.75, mt: 0.25 },
                      '& ol': { pl: 2.25, mb: 0.75, mt: 0.25 },
                      '& li': { mb: 0.35, lineHeight: 1.45 },
                      '& li:last-child': { mb: 0 },
                      // Reva commonly follows a heading with financial facts.
                      // Make those lists read as compact label/value rows while
                      // preserving normal bullets for explanatory advice.
                      '& h2 + ul, & h3 + ul': {
                        listStyle: 'none',
                        pl: 0,
                        mt: 0.25,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      },
                      '& h2 + ul > li, & h3 + ul > li': {
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                        alignItems: 'baseline',
                        columnGap: 1,
                        py: 0.4,
                        mb: 0,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      },
                      '& h2 + ul > li::marker, & h3 + ul > li::marker': {
                        content: 'none',
                      },
                      '& blockquote': {
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                        pl: 2,
                        ml: 0,
                        my: 1,
                        color: 'text.secondary',
                        fontStyle: 'italic',
                      },
                      '& code': {
                        backgroundColor: 'grey.100',
                        padding: '2px 4px',
                        borderRadius: 1,
                        fontSize: '0.9em',
                        fontFamily: 'monospace',
                      },
                      '& pre': {
                        backgroundColor: 'grey.100',
                        padding: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        mb: 1,
                      },
                      '& strong': { fontWeight: 600 },
                      '& em': { fontStyle: 'italic' },
                      '& a': {
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      },
                      '& hr': { my: 2, borderColor: 'divider' },
                    }}
                  >
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Box>
                )}
              </MessageBubble>

              {message.action && renderActionCard(message.action)}

              {message.handoff && (
                <Button
                  variant="contained"
                  size="small"
                  component="a"
                  href={smsHref(message.handoff.smsBody)}
                  startIcon={<SmsOutlinedIcon />}
                  data-testid="reva-handoff-text-caroline"
                  sx={{ mt: 1.5, alignSelf: 'flex-start' }}
                >
                  {message.handoff.label}
                </Button>
              )}

              {message.objects && message.objects.length > 0 && (
                <Stack spacing={1} sx={{ width: '100%', mt: 1.5 }}>
                  {message.objects.map((object, index) => (
                    <RevaObjectCard
                      key={`${message.id}-${object.type}-${index}`}
                      object={object}
                    />
                  ))}
                </Stack>
              )}

              {message.properties && message.properties.length > 0 && (
                <Box
                  ref={(el: HTMLElement | null) => {
                    if (el) {
                      propertyListRefs.current[message.id] = el;
                    }
                  }}
                  sx={{ width: '100%', mt: 1.25 }}
                >
                  <Grid2 container spacing={1}>
                    {message.properties.map((property) => (
                      <Grid2 key={property.mlsNumber} xs={12}>
                        <Card
                          onClick={() => handlePropertyClick(property)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                            boxShadow: 'none',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            position: 'relative',
                            '&:hover': {
                              borderColor: 'primary.light',
                              '& .view-icon': { opacity: 1 },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              height: 88,
                              position: 'relative',
                            }}
                          >
                            {property.photos && property.photos.length > 0 ? (
                              <Box
                                sx={{
                                  width: 88,
                                  height: 88,
                                  flexShrink: 0,
                                  overflow: 'hidden',
                                }}
                              >
                                <img
                                  src={property.photos[0]}
                                  alt={property.address}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 88,
                                  height: 88,
                                  backgroundColor: 'grey.200',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <HomeIcon
                                  sx={{ fontSize: 30, color: 'grey.400' }}
                                />
                              </Box>
                            )}

                            <Box
                              sx={{
                                flex: 1,
                                p: 1.25,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                minWidth: 0,
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  gap: 1,
                                  mb: 0.5,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  component="div"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'primary.main',
                                    fontSize: '1rem',
                                  }}
                                >
                                  $
                                  {property.price?.toLocaleString() ||
                                    'Price N/A'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  MLS# {property.mlsNumber}
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                color="text.primary"
                                sx={{
                                  mb: 0.25,
                                  fontSize: '0.875rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {property.address}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 0.5, fontSize: '0.75rem' }}
                              >
                                {property.city}, {property.state} {property.zip}
                              </Typography>

                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  <strong>{property.beds || 0}</strong> beds
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem' }}
                                >
                                  <strong>{property.baths || 0}</strong> baths
                                </Typography>
                                {property.sqft > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.7rem' }}
                                  >
                                    <strong>
                                      {property.sqft.toLocaleString()}
                                    </strong>{' '}
                                    sqft
                                  </Typography>
                                )}
                                {property.daysOnMarket !== undefined && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.7rem' }}
                                  >
                                    {property.daysOnMarket === 0
                                      ? 'New'
                                      : `${property.daysOnMarket} days`}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            <Box
                              className="view-icon"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                              }}
                              title="View on map"
                            >
                              <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Box>
                          </Box>
                        </Card>
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  mt: 0.25,
                  px: 1,
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                }}
              >
                {message.role === 'user' ? 'You' : 'Reva'}
              </Typography>
            </Box>
          ))}

          {loading && (
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Box
                display="flex"
                alignItems="center"
                gap={0.3}
                p={1.5}
                bgcolor="grey.100"
                borderRadius={2}
              >
                {[0, 0.2, 0.4].map((delay) => (
                  <Box
                    key={delay}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'text.secondary',
                      animation: 'revaTypingDot 1.4s infinite',
                      animationDelay: `${delay}s`,
                      '@keyframes revaTypingDot': {
                        '0%, 60%, 100%': { opacity: 0.3 },
                        '30%': { opacity: 1 },
                      },
                    }}
                  />
                ))}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  px: 1,
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                }}
              >
                Reva is typing...
              </Typography>
            </Box>
          )}

          {messages.length === 1 && !loading && (
            <Stack spacing={0.75} mt={1.25}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', px: 1 }}
              >
                Try asking:
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 0.75,
                }}
              >
                {suggestions.map((suggestion) => (
                  <Chip
                    key={suggestion.question}
                    label={suggestion.label}
                    onClick={() => submitQuestion(suggestion.question)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      color: 'primary.main',
                      fontWeight: 500,
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        display: 'block',
                        lineHeight: 1.3,
                        py: 0.625,
                        px: 1,
                        fontSize: '0.75rem',
                      },
                      width: '100%',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderColor: 'primary.main',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  />
                ))}
              </Box>
            </Stack>
          )}

          <div ref={messagesEndRef} />
        </MessagesContainer>

        <Box
          sx={{
            p: 1.25,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder={askPlaceholder(briefing?.side)}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            size="small"
            multiline
            maxRows={3}
            inputRef={inputRef}
            inputProps={{
              'aria-label': 'Ask Reva',
              'data-testid': 'reva-composer',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: theme.palette.grey[50],
                fontSize: '0.875rem',
                lineHeight: 1.45,
                '& fieldset': { borderColor: theme.palette.divider },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
              '& .MuiInputBase-input': { py: 1.125 },
            }}
          />
          <Tooltip title="Send message">
            <span>
              <IconButton
                color="primary"
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                aria-label="Send to Reva"
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: 2,
                  color: 'common.white',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.grey[500],
                    backgroundColor: theme.palette.grey[200],
                  },
                }}
              >
                <SendIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </>
  );
};

export default RevaPanel;
